// Sibling and related pages that every public page links to, so each page gets more than one internal link.
// The links render at the end of `<main>` (`RelatedPages`, mounted once in the `_site` layout).
import { isCuratedApexDomain, isCuratedDomain, POPULAR_SITES } from '@/lib/popular-sites'

export type RelatedPage = { href: string; label: string }

export const COMPARE_PAGES: RelatedPage[] = [
  ['betterstack', 'Better Stack'], ['checkly', 'Checkly'], ['cronitor', 'Cronitor'], ['datadog', 'Datadog'],
  ['freshping', 'Freshping'], ['gatus', 'Gatus'], ['grafana', 'Grafana Cloud'], ['healthchecks', 'Healthchecks.io'],
  ['hetrixtools', 'HetrixTools'], ['hyperping', 'Hyperping'], ['montastic', 'Montastic'], ['newrelic', 'New Relic'],
  ['nixstats', 'Nixstats'], ['ohdear', 'Oh Dear'], ['phare', 'Phare'], ['pingdom', 'Pingdom'], ['pulsetic', 'Pulsetic'],
  ['site24x7', 'Site24x7'], ['statping', 'Statping'], ['statuscake', 'StatusCake'], ['updown', 'updown.io'],
  ['uptime-kuma', 'Uptime Kuma'], ['uptimerobot', 'UptimeRobot'], ['uptrends', 'Uptrends'],
].map(([slug, name]) => ({ href: `/compare/${slug}`, label: `Uptrack vs ${name}` }))

export const AUDIENCE_PAGES: RelatedPage[] = [
  ['agencies', 'Agencies'], ['developers', 'Developers'], ['devops', 'DevOps teams'], ['ecommerce', 'E-commerce'],
  ['freelancers', 'Freelancers'], ['indie-hackers', 'Indie hackers'], ['msp', 'MSPs'], ['non-technical', 'Non-technical teams'],
  ['nonprofits', 'Nonprofits'], ['product-teams', 'Product teams'], ['saas', 'SaaS'], ['shopify', 'Shopify stores'],
  ['sre', 'SRE teams'], ['startups', 'Startups'], ['wordpress', 'WordPress'],
].map(([slug, name]) => ({ href: `/for/${slug}`, label: `For ${name}` }))

export const FEATURE_PAGES: RelatedPage[] = [
  ['browser-monitoring', 'Browser monitoring'], ['dns-monitoring', 'DNS monitoring'], ['heartbeat-monitoring', 'Heartbeat monitoring'],
  ['keyword-monitoring', 'Keyword monitoring'], ['ping-monitoring', 'Ping monitoring'], ['ssl-monitoring', 'SSL monitoring'],
  ['status-pages', 'Status pages'], ['tcp-monitoring', 'TCP monitoring'], ['uptime-monitoring', 'Uptime monitoring'],
].map(([slug, name]) => ({ href: `/features/${slug}`, label: name }))

export const ALTERNATIVE_PAGES: RelatedPage[] = [
  ['betterstack', 'Better Stack'], ['checkly', 'Checkly'], ['cronitor', 'Cronitor'], ['datadog', 'Datadog'],
  ['freshping', 'Freshping'], ['grafana', 'Grafana Cloud'], ['hetrixtools', 'HetrixTools'], ['hyperping', 'Hyperping'],
  ['newrelic', 'New Relic'], ['pingdom', 'Pingdom'], ['pulsetic', 'Pulsetic'], ['site24x7', 'Site24x7'],
  ['statuscake', 'StatusCake'], ['uptime-kuma', 'Uptime Kuma'], ['uptimerobot', 'UptimeRobot'],
].map(([slug, name]) => ({ href: `/alternatives/${slug}`, label: `${name} alternative` }))

/** The status code pages, with the name each page uses as its title. */
export const HTTP_STATUS_NAMES: Record<number, string> = {
  200: 'OK', 201: 'Created', 204: 'No Content', 301: 'Moved Permanently', 302: 'Found', 304: 'Not Modified',
  400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found', 405: 'Method Not Allowed',
  408: 'Request Timeout', 409: 'Conflict', 410: 'Gone', 413: 'Payload Too Large', 415: 'Unsupported Media Type',
  422: 'Unprocessable Entity', 429: 'Too Many Requests', 451: 'Unavailable For Legal Reasons',
  500: 'Internal Server Error', 502: 'Bad Gateway', 503: 'Service Unavailable', 504: 'Gateway Timeout',
  520: 'Web Server Returns Unknown Error', 522: 'Connection Timed Out',
}

export const GLOSSARY_PAGES: RelatedPage[] = [
  ['alert-fatigue', 'Alert fatigue'], ['dns-propagation', 'DNS propagation'], ['downtime', 'Downtime'],
  ['error-budget', 'Error budget'], ['escalation-policy', 'Escalation policy'], ['five-nines', 'Five nines (99.999% uptime)'],
  ['heartbeat-monitoring', 'Heartbeat monitoring'], ['incident-management', 'Incident management'], ['latency', 'Latency'],
  ['mtbf', 'MTBF'], ['mttd', 'MTTD'], ['mttf', 'MTTF'], ['mttr', 'MTTR'], ['on-call', 'On-call'],
  ['real-user-monitoring', 'Real user monitoring'], ['sla', 'SLA'], ['ssl-certificate', 'SSL certificate'],
  ['status-page', 'Status page'], ['synthetic-monitoring', 'Synthetic monitoring'], ['uptime', 'Uptime'],
].map(([slug, name]) => ({ href: `/glossary/${slug}`, label: name }))

export const INTEGRATION_PAGES: RelatedPage[] = [
  { href: '/integrations/slack', label: 'Slack alerts' },
  { href: '/integrations/discord', label: 'Discord alerts' },
  { href: '/integrations/telegram', label: 'Telegram alerts' },
  { href: '/integrations/email', label: 'Email alerts' },
  { href: '/integrations/n8n', label: 'n8n node' },
  { href: '/guides/webhook-payloads', label: 'Webhook payloads' },
]

/** Descriptive link text for every page the reading lists point at. */
const LABELS: Record<string, string> = {
  '/ai': 'Uptrack for AI agents and MCP',
  '/cron-monitoring': 'Cron job monitoring',
  '/free-uptime-monitoring': 'Free uptime monitoring',
  '/pricing': 'Uptrack pricing',
  '/features/uptime-monitoring': 'Uptime monitoring',
  '/features/keyword-monitoring': 'Keyword monitoring',
  '/features/browser-monitoring': 'Browser monitoring',
  '/features/dns-monitoring': 'DNS monitoring',
  '/features/status-pages': 'Status pages',
  '/features/heartbeat-monitoring': 'Heartbeat monitoring',
  '/for/developers': 'Uptrack for developers',
  '/for/indie-hackers': 'Uptrack for indie hackers',
  '/for/nonprofits': 'Uptrack for nonprofits',
  '/for/saas': 'Uptrack for SaaS',
  '/for/agencies': 'Uptrack for agencies',
  '/tools/is-website-down': 'Is a website down?',
  '/tools/browser-check': 'Free browser check',
  '/tools/ssl-checker': 'SSL certificate checker',
  '/tools/uptime-calculator': 'Uptime calculator',
  '/tools/sla-calculator': 'SLA calculator',
  '/tools/downtime-cost-calculator': 'Downtime cost calculator',
  '/tools/txt-record-lookup': 'TXT record lookup',
  '/tools/whois-lookup': 'WHOIS lookup',
  '/status/uptrack': 'Uptrack status page',
  '/blog/dns-monitoring-guide': 'DNS monitoring guide',
  '/blog/downtime-seo-impact': 'How downtime affects SEO rankings',
  '/blog/free-pro-for-nonprofits': 'Uptrack Pro is free for nonprofits',
  '/blog/freshping-discord-integration-alternative': 'Freshping Discord alerts after the shutdown',
  '/blog/how-to-check-if-a-website-is-down-from-multiple-locations': 'Check if a website is down from multiple locations',
  '/blog/http-451-unavailable-for-legal-reasons': 'HTTP 451 Unavailable For Legal Reasons',
  '/blog/typst-for-pdf-generation': 'Why Uptrack uses Typst for PDF reports',
  '/guides/sms-and-call-alerts-faq': 'SMS and phone call alerts: countries and limits',
  '/guides/ssl-monitoring': 'Check SSL certificate expiration',
  '/features/ssl-monitoring': 'SSL monitoring',
  '/tools/dns-lookup': 'DNS lookup',
  '/blog/22-check-locations': 'How 24 check locations avoid false alarms',
  '/blog/best-free-uptime-monitoring-tools': 'Best free uptime monitoring tools',
  '/blog/free-uptime-monitoring-europe-gdpr': 'Free uptime monitoring in Europe and GDPR',
  '/blog/multi-region-consensus': 'How multi-region consensus works',
  '/blog/synthetic-monitoring-services-guide': 'How to choose a synthetic monitoring service',
  '/blog/uptime-monitoring-indie-hackers': 'Uptime monitoring for indie hackers',
  '/blog/uptrack-in-claude-connector-directory': 'Ask Claude which of your sites are down',
  '/blog/what-is-mtbf-meaning-formula': 'What is MTBF? Meaning and formula',
  '/blog/what-is-uptime-monitoring': 'What is uptime monitoring?',
  '/blog/what-is-uptime-sla': 'What is a good uptime SLA?',
  '/blog/why-alert-confirmation': 'Why most uptime alerts are noise',
  '/blog/why-beam': 'Why Uptrack runs on the BEAM',
  '/guides/api-monitoring': 'API uptime monitoring',
  '/guides/browser-checks': 'Browser checks guide',
  '/guides/mcp': 'Uptrack MCP server guide',
  '/guides/monitor-saas-landing-page': 'Monitor a SaaS landing page',
  '/guides/monitor-side-project': 'Monitor a side project',
  '/guides/monitoring-as-code': 'Monitoring as code with the CLI',
  '/guides/slack-commands': 'Slack slash commands',
  '/guides/status-pages': 'Public status pages guide',
  '/guides/webhook-payloads': 'Webhook payload reference',
  '/guides/zapier': 'Connect Uptrack to Zapier',
  '/guides/switch-from-pingdom': 'Switch from Pingdom',
  '/guides/switch-from-uptimerobot': 'Switch from UptimeRobot',
  '/compare/uptimerobot': 'Uptrack vs UptimeRobot',
  '/compare/freshping': 'Uptrack vs Freshping',
  '/alternatives/uptimerobot': 'UptimeRobot alternative',
  '/alternatives/freshping': 'Freshping alternative',
  '/integrations/slack': 'Slack alerts',
  '/integrations/discord': 'Discord alerts',
  '/integrations/email': 'Email alerts',
  '/integrations/n8n': 'n8n node',
  '/glossary/alert-fatigue': 'Alert fatigue',
  '/glossary/dns-propagation': 'DNS propagation',
  '/glossary/downtime': 'Downtime',
  '/glossary/escalation-policy': 'Escalation policy',
  '/glossary/five-nines': 'Five nines (99.999% uptime)',
  '/glossary/latency': 'Latency',
  '/glossary/mtbf': 'MTBF',
  '/glossary/mttr': 'MTTR',
  '/glossary/on-call': 'On-call',
  '/glossary/sla': 'SLA',
  '/glossary/ssl-certificate': 'SSL certificate',
  '/glossary/status-page': 'Status page',
  '/glossary/synthetic-monitoring': 'Synthetic monitoring',
  '/http/403': 'HTTP 403 Forbidden',
  '/http/404': 'HTTP 404 Not Found',
  '/http/451': 'HTTP 451 Unavailable For Legal Reasons',
  '/http/500': 'HTTP 500 Internal Server Error',
}

/** Link text for a page: the explicit text above, else the text the sibling lists already use. */
function labelFor(href: string): string {
  const listed = [...COMPARE_PAGES, ...ALTERNATIVE_PAGES, ...AUDIENCE_PAGES, ...FEATURE_PAGES, ...GLOSSARY_PAGES, ...INTEGRATION_PAGES]
  return LABELS[href] ?? listed.find((p) => p.href === href)?.label ?? href
}

/**
 * Hand-picked reading for the blog posts and guides. Each page links to 3 or 4 pages on the same topic:
 * its product page, a tool, a term, a sibling article. Link text comes from `LABELS`.
 */
export const READING: Record<string, string[]> = {
  '/blog/22-check-locations': ['/blog/multi-region-consensus', '/blog/why-alert-confirmation', '/features/uptime-monitoring', '/tools/is-website-down'],
  '/blog/best-free-uptime-monitoring-tools': ['/free-uptime-monitoring', '/blog/free-uptime-monitoring-europe-gdpr', '/compare/uptimerobot', '/alternatives/uptimerobot'],
  '/blog/dns-monitoring-guide': ['/features/dns-monitoring', '/tools/txt-record-lookup', '/tools/whois-lookup', '/blog/what-is-uptime-monitoring'],
  '/blog/downtime-seo-impact': ['/blog/what-is-uptime-monitoring', '/glossary/downtime', '/tools/downtime-cost-calculator', '/features/uptime-monitoring'],
  '/blog/free-pro-for-nonprofits': ['/for/nonprofits', '/features/status-pages', '/free-uptime-monitoring', '/blog/free-uptime-monitoring-europe-gdpr'],
  '/blog/free-uptime-monitoring-europe-gdpr': ['/free-uptime-monitoring', '/features/uptime-monitoring', '/blog/best-free-uptime-monitoring-tools', '/blog/free-pro-for-nonprofits'],
  '/blog/freshping-discord-integration-alternative': ['/integrations/discord', '/alternatives/freshping', '/compare/freshping', '/free-uptime-monitoring'],
  '/blog/how-to-check-if-a-website-is-down-from-multiple-locations': ['/tools/is-website-down', '/blog/22-check-locations', '/glossary/downtime', '/features/uptime-monitoring'],
  '/blog/http-451-unavailable-for-legal-reasons': ['/http/451', '/http/403', '/http/404', '/tools/is-website-down'],
  '/blog/multi-region-consensus': ['/blog/22-check-locations', '/blog/why-alert-confirmation', '/blog/why-beam', '/features/uptime-monitoring'],
  '/blog/synthetic-monitoring-services-guide': ['/features/browser-monitoring', '/guides/browser-checks', '/glossary/synthetic-monitoring', '/tools/browser-check'],
  '/blog/typst-for-pdf-generation': ['/blog/why-beam', '/blog/multi-region-consensus', '/blog/22-check-locations'],
  '/blog/uptime-monitoring-indie-hackers': ['/for/indie-hackers', '/guides/monitor-side-project', '/guides/monitor-saas-landing-page', '/free-uptime-monitoring'],
  '/blog/uptrack-in-claude-connector-directory': ['/ai', '/guides/mcp', '/guides/monitoring-as-code', '/tools/is-website-down'],
  '/blog/what-is-mtbf-meaning-formula': ['/glossary/mtbf', '/glossary/mttr', '/blog/what-is-uptime-sla', '/tools/uptime-calculator'],
  '/blog/what-is-uptime-monitoring': ['/features/uptime-monitoring', '/guides/monitor-side-project', '/glossary/downtime', '/free-uptime-monitoring'],
  '/blog/what-is-uptime-sla': ['/glossary/sla', '/glossary/five-nines', '/tools/sla-calculator', '/tools/uptime-calculator'],
  '/blog/why-alert-confirmation': ['/blog/22-check-locations', '/blog/multi-region-consensus', '/glossary/alert-fatigue', '/features/uptime-monitoring'],
  '/blog/why-beam': ['/blog/multi-region-consensus', '/blog/why-alert-confirmation', '/blog/22-check-locations', '/blog/typst-for-pdf-generation'],
  '/guides/api-monitoring': ['/features/keyword-monitoring', '/guides/webhook-payloads', '/glossary/latency', '/http/500'],
  '/guides/browser-checks': ['/features/browser-monitoring', '/blog/synthetic-monitoring-services-guide', '/glossary/synthetic-monitoring', '/tools/browser-check'],
  '/guides/mcp': ['/ai', '/blog/uptrack-in-claude-connector-directory', '/guides/slack-commands', '/guides/monitoring-as-code'],
  '/guides/monitor-saas-landing-page': ['/features/uptime-monitoring', '/features/keyword-monitoring', '/for/saas', '/guides/status-pages'],
  '/guides/monitor-side-project': ['/for/indie-hackers', '/free-uptime-monitoring', '/blog/uptime-monitoring-indie-hackers', '/guides/monitor-saas-landing-page'],
  '/guides/monitoring-as-code': ['/guides/mcp', '/guides/api-monitoring', '/features/uptime-monitoring', '/for/developers'],
  '/guides/slack-commands': ['/integrations/slack', '/guides/mcp', '/guides/webhook-payloads', '/guides/zapier'],
  '/guides/sms-and-call-alerts-faq': ['/integrations/email', '/glossary/on-call', '/glossary/escalation-policy', '/features/uptime-monitoring'],
  '/guides/ssl-monitoring': ['/features/ssl-monitoring', '/tools/ssl-checker', '/glossary/ssl-certificate'],
  '/guides/status-pages': ['/features/status-pages', '/glossary/status-page', '/status/uptrack', '/for/agencies'],
  '/guides/switch-from-pingdom': ['/alternatives/pingdom', '/compare/pingdom', '/guides/switch-from-uptimerobot'],
  '/guides/switch-from-uptimerobot': ['/alternatives/uptimerobot', '/compare/uptimerobot', '/guides/switch-from-pingdom'],
  '/guides/webhook-payloads': ['/guides/zapier', '/guides/slack-commands', '/integrations/n8n', '/features/uptime-monitoring'],
  '/guides/zapier': ['/guides/webhook-payloads', '/integrations/n8n', '/integrations/slack', '/guides/slack-commands'],
}

/**
 * Links from a page to the articles and pages that explain it further. They sit after the sibling links
 * (or on their own, for a page with no sibling group). Link text comes from `labelFor`.
 */
export const EXTRAS: Record<string, string[]> = {
  '/features/uptime-monitoring': ['/blog/how-to-check-if-a-website-is-down-from-multiple-locations', '/blog/downtime-seo-impact', '/guides/api-monitoring'],
  '/features/dns-monitoring': ['/blog/dns-monitoring-guide'],
  '/features/browser-monitoring': ['/blog/synthetic-monitoring-services-guide', '/guides/browser-checks'],
  '/features/status-pages': ['/guides/status-pages'],
  '/features/keyword-monitoring': ['/guides/api-monitoring'],
  '/features/ssl-monitoring': ['/guides/ssl-monitoring'],
  '/features/heartbeat-monitoring': ['/cron-monitoring'],
  '/for/nonprofits': ['/blog/free-pro-for-nonprofits'],
  '/for/indie-hackers': ['/blog/uptime-monitoring-indie-hackers', '/guides/monitor-side-project'],
  '/for/saas': ['/guides/monitor-saas-landing-page'],
  '/for/developers': ['/guides/monitoring-as-code', '/guides/api-monitoring'],
  '/compare/pingdom': ['/guides/switch-from-pingdom'],
  '/alternatives/pingdom': ['/guides/switch-from-pingdom'],
  '/compare/uptimerobot': ['/guides/switch-from-uptimerobot'],
  '/alternatives/uptimerobot': ['/guides/switch-from-uptimerobot'],
  '/compare/freshping': ['/blog/freshping-discord-integration-alternative'],
  '/alternatives/freshping': ['/blog/freshping-discord-integration-alternative'],
  '/glossary/mtbf': ['/blog/what-is-mtbf-meaning-formula'],
  '/glossary/mttr': ['/blog/what-is-mtbf-meaning-formula'],
  '/glossary/sla': ['/blog/what-is-uptime-sla'],
  '/glossary/five-nines': ['/blog/what-is-uptime-sla'],
  '/glossary/downtime': ['/blog/downtime-seo-impact'],
  '/glossary/dns-propagation': ['/blog/dns-monitoring-guide'],
  '/glossary/synthetic-monitoring': ['/blog/synthetic-monitoring-services-guide'],
  '/glossary/ssl-certificate': ['/guides/ssl-monitoring'],
  '/glossary/on-call': ['/guides/sms-and-call-alerts-faq'],
  '/glossary/escalation-policy': ['/guides/sms-and-call-alerts-faq'],
  '/glossary/status-page': ['/guides/status-pages'],
  '/http/451': ['/blog/http-451-unavailable-for-legal-reasons'],
  '/http/403': ['/blog/http-451-unavailable-for-legal-reasons'],
  '/http/410': ['/blog/http-451-unavailable-for-legal-reasons'],
  '/integrations/email': ['/guides/sms-and-call-alerts-faq'],
  '/integrations/slack': ['/guides/slack-commands'],
  '/ai': ['/blog/uptrack-in-claude-connector-directory', '/guides/mcp'],
  '/free-uptime-monitoring': ['/blog/best-free-uptime-monitoring-tools', '/blog/free-uptime-monitoring-europe-gdpr', '/blog/free-pro-for-nonprofits'],
  '/tools/is-website-down': ['/blog/how-to-check-if-a-website-is-down-from-multiple-locations'],
  '/tools/uptime-calculator': ['/blog/what-is-uptime-sla'],
  '/tools/sla-calculator': ['/blog/what-is-uptime-sla', '/blog/what-is-mtbf-meaning-formula'],
  '/tools/downtime-cost-calculator': ['/blog/downtime-seo-impact'],
  '/tools/dns-lookup': ['/blog/dns-monitoring-guide'],
}

/** The per-domain tool pages. A curated domain has a page for each tool; the registry tools need a registrable domain. */
export type DomainChecks = { isDown: boolean; dns: boolean; ssl: boolean; expiry: boolean }

const DOMAIN_TOOLS = [
  { slug: 'is-website-down', label: (n: string) => `Is ${n} down?` },
  { slug: 'dns-lookup', label: (n: string) => `DNS records of ${n}` },
  { slug: 'ssl-checker', label: (n: string) => `SSL certificate of ${n}` },
  { slug: 'domain-expiry-checker', label: (n: string) => `Domain expiry of ${n}` },
] as const

function domainChecks(path: string): { heading: string; pages: RelatedPage[] } | null {
  const m = path.match(/^\/tools\/(is-website-down|dns-lookup|ssl-checker|domain-expiry-checker)\/([^/]+)$/)
  if (!m) return null
  const domain = m[2].toLowerCase()
  if (!isCuratedDomain(domain)) return null
  const name = POPULAR_SITES.find((s) => s.domain === domain)?.name ?? domain
  const pages = DOMAIN_TOOLS
    .filter((t) => t.slug !== m[1] && (t.slug !== 'domain-expiry-checker' || isCuratedApexDomain(domain)))
    .map((t) => ({ href: `/tools/${t.slug}/${domain}`, label: t.label(name) }))
  return { heading: `More checks for ${name}`, pages }
}

export type RelatedGroup = { heading: string; pages: RelatedPage[] }
/** What a page links to: its sibling pages, and the articles that explain it further. */
export type Related = RelatedGroup & { more?: RelatedGroup }

/** The pages to link from `pathname`, without the page itself. */
export function relatedPagesFor(pathname: string): Related | null {
  const path = pathname.replace(/\/$/, '')
  const group = groupFor(path)
  const extra = (EXTRAS[path] ?? []).map((href) => ({ href, label: labelFor(href) }))
  if (!extra.length) return group
  if (!group) return { heading: 'Keep reading', pages: extra }
  return { ...group, more: { heading: 'Keep reading', pages: extra.filter((e) => !group.pages.some((p) => p.href === e.href)) } }
}

function groupFor(path: string): RelatedGroup | null {
  const without = (pages: RelatedPage[]) => pages.filter((p) => p.href !== path)
  const slug = path.split('/').pop() ?? ''
  const reading = READING[path]
  if (reading) {
    return { heading: 'Related reading', pages: reading.map((href) => ({ href, label: labelFor(href) })) }
  }
  const perDomain = domainChecks(path)
  if (perDomain) return perDomain
  if (path.startsWith('/compare/')) {
    const alt = ALTERNATIVE_PAGES.find((p) => p.href === `/alternatives/${slug}`)
    return { heading: 'Other comparisons', pages: [...(alt ? [alt] : []), ...without(COMPARE_PAGES)] }
  }
  if (path.startsWith('/alternatives/')) {
    const cmp = COMPARE_PAGES.find((p) => p.href === `/compare/${slug}`)
    return { heading: 'Other alternatives', pages: [...(cmp ? [cmp] : []), ...without(ALTERNATIVE_PAGES)] }
  }
  if (path.startsWith('/for/')) return { heading: 'Uptrack for other teams', pages: without(AUDIENCE_PAGES) }
  if (path.startsWith('/features/')) return { heading: 'Other features', pages: without(FEATURE_PAGES) }
  if (/^\/http\/\d+$/.test(path)) {
    // Same class first, nearest code first (a 404 sits next to 403 and 410), then the other classes.
    const own = Number(slug)
    const all = Object.entries(HTTP_STATUS_NAMES)
      .filter(([code]) => code !== slug)
      .map(([code, name]) => ({ href: `/http/${code}`, label: `HTTP ${code} ${name}`, code: Number(code), same: Math.floor(Number(code) / 100) === Math.floor(own / 100) }))
      .sort((a, b) => Number(b.same) - Number(a.same) || Math.abs(a.code - own) - Math.abs(b.code - own))
    return { heading: 'Other HTTP status codes', pages: all.map(({ href, label }) => ({ href, label })) }
  }
  if (/^\/glossary\/[^/]+$/.test(path)) return { heading: 'More glossary terms', pages: without(GLOSSARY_PAGES) }
  if (/^\/integrations\/[^/]+$/.test(path)) return { heading: 'Other integrations', pages: without(INTEGRATION_PAGES) }
  return null
}
